/**
 * Writes an SPSS system file (.sav), the format SPSS saves in, as PSPP documents it ("System File
 * Format"). Bytecode-compressed and UTF-8, like the files IBM's own library writes.
 *
 * Knows nothing about surveys: whoever calls it decides the variables, labels and formats. The
 * limits SPSS itself imposes are kept here -- 64-byte names, 255-byte variable labels, 120-byte value
 * labels -- and strings longer than 255 bytes are written whole, as SPSS's "very long strings".
 */

export interface SavFormat {
  type: number;
  width: number;
  decimals: number;
}

/** The format types this writer is given. SPSS has dozens more (dollars, dates of other shapes...). */
export const SAV_FORMAT_TYPE = { string: 1, number: 5, dateTime: 22 } as const;
export const SAV_MEASURE = { unknown: 0, nominal: 1, ordinal: 2, scale: 3 } as const;
export const SAV_ALIGNMENT = { left: 0, right: 1, centre: 2 } as const;

export interface SavColumn {
  name: string;
  label?: string;
  /** 0 for a number; otherwise the string's width in bytes, up to 32767. */
  width: number;
  format: SavFormat;
  measure: number;
  alignment: number;
  displayWidth: number;
  valueLabels?: { value: number; label: string }[];
  attributes?: Record<string, string[]>;
}

/** A multiple dichotomy set: columns that each hold `countedValue` where the answer was picked. */
export interface SavDichotomySet {
  name: string;
  label: string;
  countedValue: string;
  columns: string[];
}

export interface SavDocument {
  fileLabel?: string;
  created: Date;
  columns: SavColumn[];
  /** Per row, a value per column: a number, null for system-missing, or a string column's text. */
  rows: (number | string | null)[][];
  /** The column SPSS weights cases by when the file is opened. */
  weight?: string;
  multipleResponseSets?: SavDichotomySet[];
}

const SYSMIS = -Number.MAX_VALUE;
const BIAS = 100;
const MAX_SHORT_STRING = 255;
/** Of each 255-byte segment of a very long string, all but the last 3 bytes hold the string. */
const SEGMENT_BYTES = 252;
const MAX_NAME_BYTES = 64;
const MAX_VARIABLE_LABEL_BYTES = 255;
const MAX_VALUE_LABEL_BYTES = 120;
const FILE_LABEL_BYTES = 64;
const PRODUCT = '@(#) SPSS DATA FILE Navigator';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RESERVED_NAMES = new Set(['ALL', 'AND', 'BY', 'EQ', 'GE', 'GT', 'LE', 'LT', 'NE', 'NOT', 'OR', 'TO', 'WITH']);

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Cut to at most `maxBytes` of UTF-8 without splitting a character. */
export function truncateUtf8(text: string, maxBytes: number): string {
  const bytes = encoder.encode(text);
  if (bytes.length <= maxBytes) return text;
  let end = maxBytes;
  while (end > 0 && (bytes[end] & 0xc0) === 0x80) end--;
  return decoder.decode(bytes.subarray(0, end));
}

/**
 * A name SPSS accepts: starting with a letter (or @, #, $), then letters, digits and . _ $ # @, at
 * most 64 bytes, not ending in a full stop and not one of its reserved words. SPSS allows letters
 * beyond ASCII too; survey platforms don't use them, so anything else becomes an underscore.
 */
export function savName(name: string): string {
  let safe = name.replace(/[^A-Za-z0-9._$#@]/g, '_');
  if (!/^[A-Za-z@#$]/.test(safe)) safe = `v${safe}`;
  safe = truncateUtf8(safe, MAX_NAME_BYTES).replace(/\.+$/, '');
  return RESERVED_NAMES.has(safe.toUpperCase()) ? `${safe}_` : safe;
}

class Bytes {
  private readonly chunks: Uint8Array[] = [];
  private size = 0;

  int32(value: number): void {
    const chunk = new Uint8Array(4);
    new DataView(chunk.buffer).setInt32(0, value, true);
    this.add(chunk);
  }

  float64(value: number): void {
    const chunk = new Uint8Array(8);
    new DataView(chunk.buffer).setFloat64(0, value, true);
    this.add(chunk);
  }

  add(chunk: Uint8Array): void {
    this.chunks.push(chunk);
    this.size += chunk.length;
  }

  /** Text padded with spaces (or cut) to exactly `length` bytes. */
  fixed(text: string, length: number): void {
    const chunk = new Uint8Array(length).fill(0x20);
    chunk.set(encoder.encode(truncateUtf8(text, length)));
    this.add(chunk);
  }

  /** An extension record: subtype 7 and its items. */
  extension(subtype: number, itemSize: number, body: Uint8Array): void {
    this.int32(7);
    this.int32(subtype);
    this.int32(itemSize);
    this.int32(body.length / itemSize);
    this.add(body);
  }

  join(): Uint8Array {
    const joined = new Uint8Array(this.size);
    let at = 0;
    for (const chunk of this.chunks) {
      joined.set(chunk, at);
      at += chunk.length;
    }
    return joined;
  }
}

interface Segment {
  /** 0 for a number, else this segment's string width. */
  width: number;
  /** 8-byte slots this segment takes in a case. */
  slots: number;
  shortName: string;
  firstSlot: number;
}

interface Laid {
  column: SavColumn;
  name: string;
  segments: Segment[];
}

function packFormat(format: SavFormat): number {
  return (format.type << 16) | (format.width << 8) | format.decimals;
}

function segmentWidths(width: number): number[] {
  if (width <= MAX_SHORT_STRING) return [width];
  const count = Math.ceil(width / SEGMENT_BYTES);
  return Array.from({ length: count }, (_, i) => (i < count - 1 ? MAX_SHORT_STRING : width - i * SEGMENT_BYTES));
}

/**
 * Every variable also needs an 8-byte name, which the records that predate long names refer to it
 * by. The first 8 bytes in capitals where that's free, otherwise `V<position>_A` -- what SPSS itself
 * generates, and what Decipher's files carry.
 */
function layOut(columns: SavColumn[]): Laid[] {
  const taken = new Set<string>();
  const claim = (preferred: string, position: number): string => {
    let candidate = preferred;
    for (let suffix = 0; !candidate || taken.has(candidate); suffix++) {
      candidate = `V${position}_${String.fromCharCode(65 + (suffix % 26))}${suffix >= 26 ? Math.floor(suffix / 26) : ''}`;
    }
    taken.add(candidate);
    return candidate;
  };

  const usedNames = new Set<string>();
  let slot = 0;
  return columns.map((column, i) => {
    let name = savName(column.name);
    for (let n = 2; usedNames.has(name.toUpperCase()); n++) name = savName(`${column.name}_${n}`);
    usedNames.add(name.toUpperCase());

    const segments = segmentWidths(column.width).map((width, s) => {
      const shortName = claim(s === 0 ? truncateUtf8(name, 8).toUpperCase() : '', i + 1);
      const slots = width === 0 ? 1 : Math.ceil(width / 8);
      const segment = { width, slots, shortName, firstSlot: slot };
      slot += slots;
      return segment;
    });
    return { column, name, segments };
  });
}

function stamp(date: Date): { day: string; time: string } {
  const two = (n: number) => String(n).padStart(2, '0');
  return {
    day: `${two(date.getDate())} ${MONTHS[date.getMonth()]} ${two(date.getFullYear() % 100)}`,
    time: `${two(date.getHours())}:${two(date.getMinutes())}:${two(date.getSeconds())}`,
  };
}

export function writeSav(document: SavDocument): Uint8Array {
  const laid = layOut(document.columns);
  const slots = laid.reduce((total, variable) => total + variable.segments.reduce((sum, s) => sum + s.slots, 0), 0);
  const weightIndex = document.weight
    ? (laid.find(variable => variable.column.name === document.weight)?.segments[0].firstSlot ?? -1) + 1
    : 0;
  const out = new Bytes();

  const { day, time } = stamp(document.created);
  out.add(encoder.encode('$FL2'));
  out.fixed(PRODUCT, 60);
  out.int32(2);
  out.int32(slots);
  out.int32(1);
  out.int32(weightIndex);
  out.int32(document.rows.length);
  out.float64(BIAS);
  out.fixed(day, 9);
  out.fixed(time, 8);
  out.fixed(document.fileLabel ?? '', FILE_LABEL_BYTES);
  out.add(new Uint8Array(3));

  for (const variable of laid) writeVariable(out, variable);
  for (const variable of laid) writeValueLabels(out, variable);
  writeExtensions(out, laid, document);
  out.int32(999);
  out.int32(0);
  writeCases(out, laid, document.rows);
  return out.join();
}

function writeVariable(out: Bytes, { column, segments }: Laid): void {
  segments.forEach((segment, s) => {
    const label = s === 0 && column.label ? encoder.encode(truncateUtf8(column.label, MAX_VARIABLE_LABEL_BYTES)) : null;
    const format = segment.width
      ? packFormat({ type: SAV_FORMAT_TYPE.string, width: segment.width, decimals: 0 })
      : packFormat(column.format);
    out.int32(2);
    out.int32(segment.width);
    out.int32(label ? 1 : 0);
    out.int32(0);
    out.int32(format);
    out.int32(format);
    out.fixed(segment.shortName, 8);
    if (label) {
      out.int32(label.length);
      const padded = new Uint8Array(Math.ceil(label.length / 4) * 4);
      padded.set(label);
      out.add(padded);
    }
    // A string longer than 8 bytes takes one record per further 8-byte slot.
    for (let i = 1; i < segment.slots; i++) {
      out.int32(2);
      out.int32(-1);
      out.int32(0);
      out.int32(0);
      out.int32(0);
      out.int32(0);
      out.fixed('', 8);
    }
  });
}

function writeValueLabels(out: Bytes, { column, segments }: Laid): void {
  if (column.width || !column.valueLabels?.length) return;
  out.int32(3);
  out.int32(column.valueLabels.length);
  for (const { value, label } of column.valueLabels) {
    out.float64(value);
    const text = encoder.encode(truncateUtf8(label, MAX_VALUE_LABEL_BYTES));
    const entry = new Uint8Array(Math.ceil((text.length + 1) / 8) * 8).fill(0x20);
    entry[0] = text.length;
    entry.set(text, 1);
    out.add(entry);
  }
  out.int32(4);
  out.int32(1);
  out.int32(segments[0].firstSlot + 1);
}

function textBody(text: string): Uint8Array {
  return encoder.encode(text);
}

function writeExtensions(out: Bytes, laid: Laid[], document: SavDocument): void {
  const integers = new Uint8Array(32);
  const integerView = new DataView(integers.buffer);
  // Version 20.0.0, machine unknown, IEEE floats, compressed, little-endian, UTF-8.
  [20, 0, 0, -1, 1, 1, 2, 65001].forEach((value, i) => integerView.setInt32(i * 4, value, true));
  out.extension(3, 4, integers);

  const floats = new Uint8Array(24);
  const floatView = new DataView(floats.buffer);
  [SYSMIS, Number.MAX_VALUE, -Number.MAX_VALUE].forEach((value, i) => floatView.setFloat64(i * 8, value, true));
  out.extension(4, 8, floats);

  const shortNameOf = new Map(laid.map(variable => [variable.column.name, variable.segments[0].shortName]));
  const sets = (document.multipleResponseSets ?? []).filter(set => set.columns.length > 1);
  if (sets.length) {
    const lines = sets.map(set => {
      const label = truncateUtf8(set.label, MAX_VARIABLE_LABEL_BYTES);
      const names = set.columns.map(column => (shortNameOf.get(column) ?? '').toLowerCase()).join(' ');
      return `$${set.name}=D${set.countedValue.length} ${set.countedValue} ${encoder.encode(label).length} ${label} ${names}\n`;
    });
    out.extension(7, 1, textBody(lines.join('')));
  }

  const display = new Uint8Array(laid.reduce((total, variable) => total + variable.segments.length, 0) * 12);
  const displayView = new DataView(display.buffer);
  let entry = 0;
  for (const { column, segments } of laid) {
    for (let s = 0; s < segments.length; s++) {
      displayView.setInt32(entry * 12, column.measure, true);
      displayView.setInt32(entry * 12 + 4, column.displayWidth, true);
      displayView.setInt32(entry * 12 + 8, column.alignment, true);
      entry++;
    }
  }
  out.extension(11, 4, display);

  out.extension(13, 1, textBody(laid.map(variable => `${variable.segments[0].shortName}=${variable.name}`).join('\t')));

  const veryLong = laid.filter(variable => variable.segments.length > 1);
  if (veryLong.length) {
    out.extension(14, 1, textBody(veryLong
      .map(variable => `${variable.segments[0].shortName}=${String(variable.column.width).padStart(5, '0')}\0\t`)
      .join('')));
  }

  const count = new Uint8Array(16);
  const countView = new DataView(count.buffer);
  countView.setInt32(0, 1, true);
  countView.setUint32(8, document.rows.length % 2 ** 32, true);
  countView.setUint32(12, Math.floor(document.rows.length / 2 ** 32), true);
  out.extension(16, 8, count);

  const attributed = laid.filter(variable => Object.keys(variable.column.attributes ?? {}).length);
  if (attributed.length) {
    out.extension(18, 1, textBody(attributed.map(({ column, name }) => `${name}:${
      Object.entries(column.attributes ?? {}).map(([attribute, values]) =>
        `${attribute}(${values.map(value => `'${value}'\n`).join('')})`).join('')
    }`).join('/')));
  }

  out.extension(20, 1, textBody('UTF-8'));
}

/**
 * Bytecode compression: each run of eight one-byte codes is followed by the 8-byte values the codes
 * don't hold themselves. A whole number from -99 to 151 is its own code (plus the bias of 100), 254
 * is eight spaces, 255 is system-missing, and 253 means "the next raw value".
 */
function writeCases(out: Bytes, laid: Laid[], rows: (number | string | null)[][]): void {
  const spaces = new Uint8Array(8).fill(0x20);
  let codes: number[] = [];
  let raw: Uint8Array[] = [];
  const flush = () => {
    const block = new Uint8Array(8);
    block.set(codes);
    out.add(block);
    raw.forEach(value => out.add(value));
    codes = [];
    raw = [];
  };
  const code = (value: number, rawValue?: Uint8Array) => {
    codes.push(value);
    if (rawValue) raw.push(rawValue);
    if (codes.length === 8) flush();
  };

  for (const row of rows) {
    laid.forEach(({ column, segments }, c) => {
      const value = row[c];
      if (!column.width) {
        if (value === null || value === undefined || typeof value === 'string' || Number.isNaN(value)) {
          code(255);
        } else if (Number.isInteger(value) && value >= 1 - BIAS && value <= 251 - BIAS) {
          code(value + BIAS);
        } else {
          const bytes = new Uint8Array(8);
          new DataView(bytes.buffer).setFloat64(0, value, true);
          code(253, bytes);
        }
        return;
      }

      const text = encoder.encode(truncateUtf8(value === null || value === undefined ? '' : String(value), column.width));
      segments.forEach((segment, s) => {
        const field = new Uint8Array(segment.slots * 8).fill(0x20);
        const start = s * SEGMENT_BYTES;
        field.set(text.subarray(start, start + (s < segments.length - 1 ? SEGMENT_BYTES : segment.width)));
        for (let i = 0; i < segment.slots; i++) {
          const slot = field.subarray(i * 8, i * 8 + 8);
          if (slot.every((byte, b) => byte === spaces[b])) code(254);
          else code(253, slot.slice());
        }
      });
    });
  }
  if (codes.length) flush();
}
